import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ICON_BASE_PATH } from "@/app/utils/config";
import UpdateuserName from "./UpdateuserName";
import { UserNameschema } from "@/app/components/common/ValidationSchema/ValidationSchema";
import { gxUserUpdateApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import type {
  UserUpdatePayload,
  UserView,
} from "@/app/types/userprofile";
import AppDrawer from "@/app/models/drawer/AppDrawer";
import { PersonalInfoLoading } from "@/app/components/webloading/shimmer/MyprofileLoading";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../../payments/initiateANewTransfer/ErrorModal";
import { DrawerNoFooter } from "@/app/models/drawer/DrawerNoFooterProps";
import ContactUs from "../../contactUs/contactUs";
import NoDataFound from "@/app/components/webloading/warning/NoDataFound";

type NameForm = {
  firstname: string;
  lastname: string;
};

interface Props {
  personalInfo: UserView | null;
  personalInfoLoading: boolean;
  fetchPersonalInfo: () => void;
}

export default function PersonalInfo({
  personalInfo,
  personalInfoLoading,
  fetchPersonalInfo,
}: Props) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const navigate = useNavigate();
  const [isAppSupportOpen, setAppSupportOpen] = useState(false);

  // ............................... toggleMobileVisibility ................................
  const toggleMobileVisibility = () => setShowMobile((prev) => !prev);

  // ............................... maskedMobile ................................
  const maskedMobile = personalInfo?.mobileNo
    ? personalInfo.mobileNo.slice(0, 4) + "*******"
    : "";

  const {
    handleSubmit,
    setValue,
    watch,
    formState: {},
  } = useForm<NameForm>({
    defaultValues: {
      firstname: "",
      lastname: "",
    },
    resolver: yupResolver(UserNameschema),
  });

  // ............................... async ................................
  const onSubmit = async (data: NameForm) => {
    if (!personalInfo) return;

    const requestPayload: UserUpdatePayload = {
      firstName: data.firstname,
      lastName: data.lastname,
      userId: personalInfo.userId,
      userCode: personalInfo.userCode,
      userPortalType: personalInfo.userPortalType,
      smeCompId: personalInfo.smeCompId,
      bankId: personalInfo.bankId,
      displayName: personalInfo.displayName,
      userDesignation: personalInfo.userDesignation,
      userLoginId: personalInfo.userLoginId,
      emailId: personalInfo.emailId,
      maskEmailId: personalInfo.maskEmailId,
      emailIdVerified: personalInfo.emailIdVerified,
      mobileCode: personalInfo.mobileCode,
      mobileNo: personalInfo.mobileNo,
      maskMobileNo: personalInfo.maskMobileNo,
      addressLine1: personalInfo.addressLine1,
      addressLine2: personalInfo.addressLine2,
      addressLine3: personalInfo.addressLine3,
      addressLine4: personalInfo.addressLine4,
      countryCode2: personalInfo.countryCode2,
      countryName: personalInfo.countryName,
      password: personalInfo.password,
      mpin: personalInfo.mpin,
      passwordExpDt: personalInfo.passwordExpDt,
      documentType: personalInfo.documentType,
      documentValue: personalInfo.documentValue,
      maskDocumentValue: personalInfo.maskDocumentValue,
      preferredLang: personalInfo.preferredLang,
      mpinEnableFlag: personalInfo.mpinEnableFlag,
      bioFaceEnableFlag: personalInfo.bioFaceEnableFlag,
      bioFingerprintEnableFlag: personalInfo.bioFingerprintEnableFlag,
      userLoginToken: personalInfo.userLoginToken,
      userLastLoginTime: personalInfo.userLastLoginTime,
      attemptLogin: personalInfo.attemptLogin,
      userType: personalInfo.userType,
      roleName: personalInfo.roleName,
      userRegistrationType: personalInfo.userRegistrationType,
      status: personalInfo.status,
      crtdDt: personalInfo.crtdDt,
      crtdIp: personalInfo.crtdIp,
      crtdBy: personalInfo.crtdBy,
      updtDt: personalInfo.updtDt,
      updtIp: personalInfo.updtIp,
      updtBy: personalInfo.updtBy,
      isRemoved: personalInfo.isRemoved,
      wkfId: personalInfo.wkfId,
      compRoleId: personalInfo.compRoleId,
      userRegisterType: personalInfo.userRegisterType,
      biometricEnableFlag: personalInfo.biometricEnableFlag,
      isBankConnect: personalInfo.isBankConnect,
      passwordEnableFlag: personalInfo.passwordEnableFlag,
      isConsentCheck: personalInfo.isConsentCheck,
      lfiHolder: personalInfo.lfiHolder,
      isKycVerified: personalInfo.isKycVerified,
      userAdmin: personalInfo.userAdmin,
      userFirstTimeLogin: personalInfo.userFirstTimeLogin,
      countryList: (personalInfo.countryList || []).map((c) => ({
        compCountryId: c.compCountryId ?? 0,
        smeCountryId: c.smeCountryId,
        countryCode2: c.countryCode2,
      })),
    };

    try {
      const response = await gxUserUpdateApi(requestPayload as any);
      if (response?.status === 0) {
        setErrorOpen(true);
        return;
      }

      if (response?.status === 1) {
        setIsDrawerOpen(false);
        fetchPersonalInfo();
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  // ............................... handleDrawerClose ................................
  const handleDrawerClose = () => setIsDrawerOpen(false);

  // ............................... handleUpdateUser ................................
  const handlesupport = () => {
    setAppSupportOpen(true);
  };

  return (
    <>
      {personalInfoLoading === true ? (
        <>
          <PersonalInfoLoading />
        </>
      ) : (
        <>
          {personalInfo ? (
            <>
              <div className="gx-profile-tab-container">
                <div className="gx-profile-title">
                  <h3 className="gx-h3-b">{t("personal_information")}</h3>
                </div>
                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img
                        src={`${ICON_BASE_PATH}userprofile.svg`}
                        alt="User"
                      />
                    </div>
                    <p className="gx-p12-sb">
                      {t("applicant_name")}
                      <span className="gx-p14-sb">
                        {personalInfo?.firstName} {personalInfo?.lastName}
                      </span>
                    </p>
                  </div>
                  <div className="information-status-child">
                    <button
                      onClick={handlesupport}
                      className="gx-p12-sb"
                      aria-label={t("contact_support")}
                    >
                      {t("contact_support")} <span>{t("to_change")}</span>
                    </button>
                  </div>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img src={`${ICON_BASE_PATH}country.svg`} alt="Country" />
                    </div>
                    <div className="information-list-mobile">
                      <p className="gx-p12-sb">
                        {t("country")}
                        <span className="gx-p14-sb">
                          {personalInfo?.countryName ||
                            personalInfo?.countryCode2}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="information-status-child">
                    <button
                      onClick={handlesupport}
                      className="gx-p12-sb"
                      aria-label={t("contact_support")}
                    >
                      {t("contact_support")} <span>{t("to_change")}</span>
                    </button>
                  </div>
                </div>

                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img src={`${ICON_BASE_PATH}email.svg`} alt="Email" />
                    </div>
                    <p className="gx-p12-sb">
                      {t("email_id")}
                      <span className="gx-p14-md">{personalInfo?.emailId}</span>
                    </p>
                  </div>
                  <div className="information-status-child">
                    <button
                      onClick={handlesupport}
                      className="gx-p12-sb"
                      aria-label={t("contact_support")}
                    >
                      {t("contact_support")} <span>{t("to_change")}</span>
                    </button>
                  </div>
                </div>
                <div className="information-list">
                  <div className="information-list-child">
                    <div className="information-list-img">
                      <img src={`${ICON_BASE_PATH}mobile.svg`} alt="Mobile" />
                    </div>
                    <div className="information-list-mobile">
                      <p className="gx-p12-sb">
                        {t("mobile_number")}
                        <span className="gx-p14-sb">
                          {showMobile ? personalInfo?.mobileNo : maskedMobile}
                        </span>
                      </p>

                      <button
                        type="button"
                        onClick={toggleMobileVisibility}
                        aria-label={
                          showMobile
                            ? "Hide mobile number"
                            : "Show mobile number"
                        }
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={`${ICON_BASE_PATH}${showMobile ? "eyeopen.svg" : "eyeclose.svg"}`}
                          alt=""
                        />
                      </button>
                    </div>
                  </div>
                  <div className="information-status-child">
                    <button
                      onClick={handlesupport}
                      className="gx-p12-sb"
                      aria-label={t("contact_support")}
                    >
                      {t("contact_support")} <span>{t("to_change")}</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <NoDataFound
                className="gx-no-data"
                message={t("no_data_found")}
              />
            </>
          )}

          <AppDrawer
            title={t("update")}
            subtitle={t("name")}
            openDrawer={isDrawerOpen}
            onClose={handleDrawerClose}
            submit={t("submit")}
            cancel={t("cancel")}
            onSubmit={handleSubmit(onSubmit)}
          >
            <UpdateuserName
              personalInfo={personalInfo}
              watch={watch}
              setValue={setValue}
            />
          </AppDrawer>
          <ErrorModal open={isErrorOpen} close={() => setErrorOpen(false)} />
          <DrawerNoFooter
            title=""
            subtitle="Contact Us"
            openDrawer={isAppSupportOpen}
            onClose={() => setAppSupportOpen(false)}
          >
            <ContactUs />
          </DrawerNoFooter>
        </>
      )}
    </>
  );
}
